<template>
  <div class="risk-flagged mt-3">
    <div class="rfm-anchor rfm-aid-sec-nona"></div>

    <div class="d-flex align-items-center justify-content-between mb-1">
      <h4 class="h5 mb-0">
        {{ tf("risk.nona_title", "Respuestas NO / No aplica") }}
        <span class="text-muted">({{ headerCount }})</span>
      </h4>

      <div class="d-flex align-items-center">
        <small class="text-muted" v-if="loadedAt">
          {{ tf("risk.last_sync", "Última carga") }}: {{ formatDate(loadedAt) }}
        </small>
        <b-button
          size="sm"
          variant="link"
          class="p-0 ml-2"
          :disabled="isHardLoading || hasAnySaving()"
          @click="manualRefresh"
        >
          Refresh
        </b-button>
      </div>
    </div>

    <div v-if="debug" class="text-muted small mb-2">{{ debugLine }}</div>

    <b-alert v-if="usingFrontFallback" show variant="warning" class="py-1 px-2 mb-2">
      Showing NO/NA from form data; follow-ups require server records.
    </b-alert>

    <b-alert v-if="loadError" show variant="danger" class="py-1 px-2 mb-2">
      {{ tf("risk.nona_load_error", "No se pudo cargar las respuestas NO/NA") }}
    </b-alert>

    <!-- Only hard loading shows a centered spinner -->
    <div v-if="isHardLoading" class="text-center my-2">
      <b-spinner label="Loading" variant="primary" />
    </div>

    <div v-show="!isHardLoading" class="rfm-relative">
      <!-- Soft loading: tiny corner indicator, pointer-events:none so it never blocks inputs -->
      <transition name="fade">
        <div v-if="isSoftLoading" class="soft-indicator">
          <b-spinner small label="Syncing" variant="secondary" />
        </div>
      </transition>

      <div class="rfm-anchor rfm-aid-sub-nona-listado"></div>

      <div v-if="!flaggedOrdered.length" class="text-muted small py-2">
        {{ tf("risk.nona_empty", "No hay preguntas marcadas como NO/NA") }}
      </div>

      <b-card
        v-for="flaggedRow in flaggedOrdered"
        :key="flaggedRow._key"
        no-body
        class="flagged-card mb-2"
      >
        <!-- Header row — clickable to expand/collapse -->
        <div
          class="flagged-header d-flex align-items-center justify-content-between"
          role="button"
          :aria-expanded="String(!!expanded[flaggedRow._key])"
          @click="toggle(flaggedRow._key)"
        >
          <div class="d-flex align-items-start flex-grow-1 pr-2">
            <div class="chev" :class="{ open: !!expanded[flaggedRow._key] }"></div>
            <div class="ml-2">
              <div class="fw-600">{{ flaggedRow.question }}</div>
              <div class="text-muted small">
                <span v-if="flaggedRow.section">{{ flaggedRow.section }}</span>
                <span v-if="flaggedRow.subsection"> / {{ flaggedRow.subsection }}</span>
              </div>
            </div>
          </div>

          <div class="text-right flex-shrink-0 ml-2">
            <span v-if="flaggedRow._pending" class="pending-badge mr-1">
              <b-spinner small style="width: 10px; height: 10px" />
              <small class="ml-1 text-muted">{{
                tf("risk.nona_pending", "guardando...")
              }}</small>
            </span>

            <small v-else class="text-muted mr-2">
              ({{ answeredCount(flaggedRow.id) }}/{{ totalFollowupsCount }})
            </small>

            <b-badge :variant="flaggedRow.answer === 'NO' ? 'danger' : 'secondary'">
              {{ flaggedRow.answer }}
            </b-badge>

            <div
              class="small text-muted mt-1"
              v-if="flaggedRow.updated_at || flaggedRow.created_at"
            >
              {{ formatDate(flaggedRow.updated_at || flaggedRow.created_at) }}
            </div>
          </div>
        </div>

        <!-- Followups body -->
        <b-collapse :id="'fa-' + flaggedRow._key" v-model="expanded[flaggedRow._key]">
          <div class="rfm-anchor rfm-aid-sub-nona-followups"></div>

          <div v-if="flaggedRow._pending" class="followups px-3 pb-3">
            <small class="text-muted">
              {{
                tf(
                  "risk.nona_pending_followups",
                  "Las preguntas de seguimiento estarán disponibles una vez guardado."
                )
              }}
            </small>
          </div>

          <div v-else class="followups">
            <div v-for="q in followupQuestionsOrdered" :key="q.id" class="followup-item">
              <label class="small fw-600 mb-1 d-flex align-items-center">
                {{ q.question }}
                <span v-if="q.is_required" class="text-danger ml-1">*</span>
              </label>

              <b-form-textarea
                :value="getFollowupAnswer(flaggedRow.id, q.id)"
                :disabled="
                  usingFrontFallback || isSaving(flaggedRow.id, q.id) || !canEdit
                "
                rows="2"
                max-rows="6"
                @input="(val) => setFollowupAnswer(flaggedRow.id, q.id, val)"
              />

              <div class="mt-1" style="min-height: 16px">
                <small v-if="isSaving(flaggedRow.id, q.id)" class="text-muted"
                  >Guardando…</small
                >
              </div>
            </div>
          </div>
        </b-collapse>
      </b-card>

      <div v-if="savedFlash" class="text-success small mt-1">{{ savedFlash }}</div>
    </div>
  </div>
</template>

<script>
import {
  BSpinner,
  BAlert,
  BFormTextarea,
  BBadge,
  BCard,
  BCollapse,
  BButton,
} from "bootstrap-vue";
import axios from "@axios";

export default {
  name: "RiskFlaggedAnswersPart",
  components: { BSpinner, BAlert, BFormTextarea, BBadge, BCard, BCollapse, BButton },

  props: {
    riskEvaluationId: { type: [Number, String], required: true },
    deferSave: { type: Boolean, default: true },
    autosaveMode: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: true },
    // null,  no preview yet, show server data only
    // [], preview received, no NO/NA items
    // [...], preview received, merge with server records
    frontFlagged: { default: null }, // intentionally no type: accepts Array|null
    debug: { type: Boolean, default: false },
  },

  data() {
    return {
      requestTimeoutMs: 45000,

      loading: true,
      loadError: false,
      loadedAt: null,
      savedFlash: "",

      flaggedById: {},
      flaggedByTemplateId: {},
      _forceRefreshT: null,
      flagged: [],
      followupQuestions: [],

      answersByFlagged: {},
      dirty: {},
      saving: {},
      expanded: {},

      loadingMode: "hard",
      lastVersionHash: "",

      riskFormAnswerId: null,
      usingFrontFallback: false,

      _autoRefreshT: null,
      _axiosInterceptorId: null,
      lastTrigger: "",
      lastRefreshAt: null,
      lastServerHash: null,
      lastLoadOk: null,
    };
  },

  computed: {
    headerCount() {
      return this.flaggedOrdered.length;
    },

    debugLine() {
      return [
        `count=${this.headerCount}`,
        `server=${this.flagged.length}`,
        `busy=${this.isBusy() ? 1 : 0}`,
        `mode=${this.loadingMode}`,
        `front=${this.usingFrontFallback ? 1 : 0}`,
        `dirty=${this._dirtyCount()}`,
        `saving=${this._savingCount()}`,
        `trigger=${this.lastTrigger || "-"}`,
      ].join(" | ");
    },

    isHardLoading() {
      return this.loadingMode === "hard";
    },
    isSoftLoading() {
      return this.loadingMode === "soft";
    },

    followupQuestionsOrdered() {
      return (Array.isArray(this.followupQuestions) ? this.followupQuestions.slice() : [])
        .filter((q) => !!q.is_active)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    },

    totalFollowupsCount() {
      return this.followupQuestionsOrdered.length;
    },

    // Merged list: frontFlagged drives WHAT to show,  server data supplies IDs and followups
    flaggedOrdered() {
      const frontItems = this.frontFlagged;

      // No preview yet or emergency fallback, server only
      if (frontItems === null || this.usingFrontFallback) {
        return (this.flagged || [])
          .slice()
          .map((row) => ({ ...row, _key: String(row.id), _pending: false }))
          .sort(
            (a, b) =>
              new Date(b.updated_at || b.created_at || 0) -
              new Date(a.updated_at || a.created_at || 0)
          );
      }

      // Merged mode
      const result = [];
      for (const fi of frontItems) {
        const tid = String(fi.template_item_id || "");
        if (!tid) continue;

        const srv = this.flaggedByTemplateId[tid] || null;
        if (srv) {
          result.push({ ...srv, _key: String(srv.id), _pending: false });
        } else {
          result.push({
            id: null,
            question: fi.question,
            section: fi.section,
            subsection: fi.subsection,
            template_item_id: tid,
            answer: String(fi.answer || "").toUpperCase(),
            created_at: null,
            updated_at: null,
            followups: [],
            id_risk_form_answer: null,
            _key: `pending-${tid}`,
            _pending: true,
          });
        }
      }

      return result.sort((a, b) => {
        if (a._pending && !b._pending) return 1;
        if (!a._pending && b._pending) return -1;
        return (
          new Date(b.updated_at || b.created_at || 0) -
          new Date(a.updated_at || a.created_at || 0)
        );
      });
    },
  },

  watch: {
    riskEvaluationId() {
      this.riskFormAnswerId = null;
      this.usingFrontFallback = false;
      this.lastVersionHash = "";
      this.lastTrigger = "riskEvaluationId";
      this.loadAll({ mode: "hard" });
    },

    frontFlagged: {
      deep: true,
      handler(newVal) {
        if (!Array.isArray(newVal)) return;
        // Pre-register expanded keys for new items so toggle works immediately
        newVal.forEach((item) => {
          const tid = String(item.template_item_id || "");
          if (!tid) return;
          const srv = this.flaggedByTemplateId[tid];
          const key = srv ? String(srv.id) : `pending-${tid}`;
          if (!(key in this.expanded)) this.$set(this.expanded, key, false);
        });
      },
    },
  },

  async mounted() {
    await this.loadAll({ mode: "hard" });
    this._installAxiosInterceptor();
  },

  beforeDestroy() {
    this._uninstallAxiosInterceptor();
    clearTimeout(this._autoRefreshT);
    clearTimeout(this._forceRefreshT);
    this._autoRefreshT = null;
  },

  methods: {
    // ─── Busy

    //soft loading is a background indicator, it does NOT block the UI
    isBusy() {
      return this.hasAnySaving() || this.isHardLoading || !!this.loading;
    },

    _dirtyCount() {
      return Object.values(this.dirty || {}).reduce(
        (n, m) => n + Object.keys(m || {}).length,
        0
      );
    },
    _savingCount() {
      return Object.values(this.saving || {}).reduce(
        (n, m) => n + Object.keys(m || {}).length,
        0
      );
    },

    // ─── Typing detection

    // Returns true when the user has focus inside a text input in THIS component
    _userIsTyping() {
      try {
        const el = document.activeElement;
        if (!el) return false;
        const tag = el.tagName.toLowerCase();
        return (tag === "textarea" || tag === "input") && !!this.$el?.contains(el);
      } catch (_) {
        return false;
      }
    },

    // ─── Auto-refresh

    //3 s debounce and skip if user is currently typing in this component
    _scheduleAutoRefresh(reason = "auto") {
      if (this.usingFrontFallback) return;
      if (!this.riskEvaluationId) return;

      this.lastTrigger = reason;
      clearTimeout(this._autoRefreshT);

      this._autoRefreshT = setTimeout(async () => {
        if (this._userIsTyping() || this.isBusy()) {
          // Reschedule quietly — try again in 3 s
          clearTimeout(this._autoRefreshT);
          this._autoRefreshT = setTimeout(async () => {
            if (!this._userIsTyping() && !this.isBusy()) {
              await this.refreshIfChanged({ mode: "soft" });
            }
          }, 3000);
          return;
        }
        await this.refreshIfChanged({ mode: "soft" });
      }, 3000);
    },

    _installAxiosInterceptor() {
      if (this._axiosInterceptorId != null) return;

      // Only react to MUTATIONS, GET requests (including our own peekServerVersion) must NEVER trigger refresh
      const MUTATIONS = new Set(["post", "put", "patch", "delete"]);
      const RX =
        /(\/risk_form_answers|\/form_answers|\/threats\/|\/threats$|\/flagged_answers(?!_follow))/i;
      const DENY = /(\/flagged_answers_follow_)/i;

      this._axiosInterceptorId = axios.interceptors.response.use(
        (res) => {
          try {
            const method = String(res?.config?.method || "").toLowerCase();
            const url = String(res?.config?.url || "");
            if (MUTATIONS.has(method) && url && RX.test(url) && !DENY.test(url)) {
              this._scheduleAutoRefresh("axios:" + url);
            }
          } catch (_) {}
          return res;
        },
        (err) => {
          try {
            const method = String(err?.config?.method || "").toLowerCase();
            const url = String(err?.config?.url || "");
            if (MUTATIONS.has(method) && url && RX.test(url) && !DENY.test(url)) {
              this._scheduleAutoRefresh("axios_err:" + url);
            }
          } catch (_) {}
          return Promise.reject(err);
        }
      );
    },

    _uninstallAxiosInterceptor() {
      if (this._axiosInterceptorId != null) {
        try {
          axios.interceptors.response.eject(this._axiosInterceptorId);
        } catch (_) {}
        this._axiosInterceptorId = null;
      }
    },

    // ─── Helpers

    tf(key, fallback = "") {
      try {
        const v = this.$t?.(key) ?? key;
        return v === key ? fallback : v;
      } catch {
        return fallback;
      }
    },

    formatDate(iso) {
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return iso;
      }
    },

    // ─── Public API (called from ShowRiskFormModal)

    manualRefresh() {
      this.lastTrigger = "manual";
      this.lastRefreshAt = new Date().toISOString();
      return this.loadAll({ mode: "hard", preserveOpen: true, preserveDirty: true });
    },

    forceRefresh({ mode = "soft" } = {}) {
      //IF the user is writting in this componen skip until he finishes
      if (this._userIsTyping()) {
        clearTimeout(this._forceRefreshT);
        this._forceRefreshT = setTimeout(() => {
          this.forceRefresh({ mode });
        }, 1500);
        return Promise.resolve();
      }
      clearTimeout(this._forceRefreshT);
      clearTimeout(this._autoRefreshT);
      this._autoRefreshT = null;
      this.lastVersionHash = "";
      this.lastRefreshAt = new Date().toISOString();
      this.lastTrigger = "force";
      return this.loadAll({ mode, preserveOpen: true, preserveDirty: true });
    },

    // ─── Data helpers
    _unwrapList(data) {
      const root = data?.data ?? data ?? [];
      if (Array.isArray(root)) return root;
      if (Array.isArray(root.data)) return root.data;
      if (Array.isArray(root.items)) return root.items;
      if (Array.isArray(root.results)) return root.results;
      const inner = root?.data ?? root?.payload ?? root?.result ?? null;
      if (Array.isArray(inner)) return inner;
      if (inner && Array.isArray(inner.data)) return inner.data;
      return [];
    },

    _onlyOwnRows(list) {
      const evaluationId = Number(this.riskEvaluationId);
      return (Array.isArray(list) ? list : []).filter((row) => {
        const rowEvaluationId =
          row?.id_risk_evaluation ?? row?.risk_evaluation_id ?? null;
        return rowEvaluationId == null || Number(rowEvaluationId) === evaluationId;
      });
    },

    _rebuildMaps() {
      const byId = {};
      const byTid = {};
      (this.flagged || []).forEach((r) => {
        byId[r.id] = r;
        if (r.template_item_id) byTid[String(r.template_item_id)] = r;
      });
      this.flaggedById = byId;
      this.flaggedByTemplateId = byTid;
    },

    _computeVersion(list = []) {
      const key = (x) =>
        `${x?.id ?? x?.template_item_id ?? ""}:${String(x?.answer ?? "").toUpperCase()}:${
          x?.updated_at || x?.created_at || ""
        }`;
      return (Array.isArray(list) ? list : []).map(key).sort().join("|");
    },

    applyFrontFallback() {
      if (!Array.isArray(this.frontFlagged) || !this.frontFlagged.length) return;
      if (Array.isArray(this.flagged) && this.flagged.length) return;

      this.usingFrontFallback = true;

      this.flagged = this.frontFlagged.map((x) => ({
        id: x.id || `virtual-${x.template_item_id}`,
        question: x.question,
        section: x.section,
        subsection: x.subsection,
        template_item_id: x.template_item_id,
        answer: String(x.answer || "").toUpperCase(),
        created_at: x.created_at || null,
        updated_at: x.updated_at || null,
        followups: [],
        id_risk_form_answer: x.id_risk_form_answer || null,
        isVirtual: true,
      }));

      this._rebuildMaps();

      const answersMap = {};
      const expandedMap = {};
      this.flagged.forEach((item) => {
        answersMap[item.id] = {};
        this.followupQuestionsOrdered.forEach((q) => {
          answersMap[item.id][q.id] = "";
        });
        expandedMap[String(item.id)] = false;
      });

      this.answersByFlagged = answersMap;
      this.expanded = { ...this.expanded, ...expandedMap };
      this.dirty = {};
      this.saving = {};
      this.loadedAt = new Date().toISOString();
    },

    // ─── Fetching

    async _ensureRiskFormAnswerId() {
      if (this.riskFormAnswerId) return this.riskFormAnswerId;
      try {
        const res = await axios.get(
          `/v1/risk_form_answers/get-form-by-evaluation/${this.riskEvaluationId}`,
          { timeout: this.requestTimeoutMs }
        );
        const payload = res?.data?.data ?? res?.data ?? {};
        for (const c of [
          payload?.id,
          payload?.risk_form_answer_id,
          payload?.id_risk_form_answer,
        ]) {
          const n = Number(c);
          if (Number.isFinite(n) && n > 0) {
            this.riskFormAnswerId = n;
            return n;
          }
        }
      } catch (_) {}
      return null;
    },

    async _fetchFlagged(params) {
      try {
        const res = await axios.get("/v1/flagged_answers", {
          timeout: this.requestTimeoutMs,
          params,
        });
        const list = this._unwrapList(res?.data);
        return Array.isArray(list) ? list : [];
      } catch {
        return [];
      }
    },

    async _fetchFlaggedFallback() {
      try {
        const res = await axios.get(`/v1/risk_evaluations/${this.riskEvaluationId}`, {
          timeout: this.requestTimeoutMs,
        });
        const evaluation = res?.data?.data ?? res?.data ?? {};
        const embedded =
          evaluation.flagged_answers ||
          evaluation.flaggedAnswers ||
          evaluation.formAnswer?.flagged_answers ||
          evaluation.formAnswer?.flaggedAnswers ||
          evaluation.form_answer?.flagged_answers ||
          evaluation.form_answer?.flaggedAnswers ||
          null;
        return Array.isArray(embedded) ? embedded : [];
      } catch {
        return [];
      }
    },

    async peekServerVersion() {
      try {
        const list = await this._fetchFlagged({
          id_risk_evaluation: this.riskEvaluationId,
          per_page: 500,
          with_followups: false,
          _ver: Date.now(),
        });
        if (!Array.isArray(list)) return null;

        const skinny = this._onlyOwnRows(list).map((x) => ({
          id: x.id,
          template_item_id: x.template_item_id,
          answer: String(x?.answer ?? "").toUpperCase(),
          created_at: x.created_at,
          updated_at: x.updated_at,
        }));
        const hash = this._computeVersion(skinny);
        this.lastServerHash = hash;
        return hash; // "" for empty list is valid
      } catch {
        return null;
      }
    },

    async refreshIfChanged({ mode = "soft" } = {}) {
      if (this.usingFrontFallback) return;

      const serverHash = await this.peekServerVersion();
      if (serverHash === null) return; // network error, we skip
      if (serverHash === this.lastVersionHash) return; // nothing changed

      this.lastRefreshAt = new Date().toISOString();
      await this.loadAll({ mode, preserveOpen: true, preserveDirty: true });
    },

    // ─── Main load
    async loadAll(opts = {}) {
      //Prevents concurrent charges, the second charge we wait until the first one is finished
      if (this._loadingLock) return;
      this._loadingLock = true;

      const { preserveOpen = true, preserveDirty = true, mode = "hard" } = opts;

      this.loadError = false;
      this.loadingMode = mode;
      this.loading = true;

      const prevExpanded = { ...(this.expanded || {}) };
      let prevDirty = {};
      let prevAnswers = {};
      try {
        prevDirty = JSON.parse(JSON.stringify(this.dirty || {}));
        prevAnswers = JSON.parse(JSON.stringify(this.answersByFlagged || {}));
      } catch (_) {}

      try {
        //Follow-up questions catalogue
        const qs = await axios.get("/v1/flagged_answers_follow_up_questions", {
          timeout: this.requestTimeoutMs,
          params: {
            is_active: true,
            per_page: 200,
            order_by: "sort_order",
            order_dir: "asc",
          },
        });
        this.followupQuestions = this._unwrapList(qs?.data);


        let list = await this._fetchFlagged({
          id_risk_evaluation: this.riskEvaluationId,
          with_followups: true,
          per_page: 500,
          _ts: Date.now(),
        });

        if (!list.length) {
          const faid = await this._ensureRiskFormAnswerId();
          if (faid)
            list = await this._fetchFlagged({
              id_risk_form_answer: faid,
              with_followups: true,
              per_page: 500,
              _ts: Date.now(),
            });
        }

        if (!list.length) list = await this._fetchFlaggedFallback();

        list = this._onlyOwnRows(list);

        // Normalise
        this.usingFrontFallback = false;
        this.flagged = (Array.isArray(list) ? list : []).map((x) => ({
          id: x.id,
          question: x.question,
          section: x.section,
          subsection: x.subsection,
          template_item_id: x.template_item_id,
          answer: String(x.answer || x.value || x.response || "").toUpperCase(),
          created_at: x.created_at,
          updated_at: x.updated_at,
          followups: Array.isArray(x.followups) ? x.followups : [],
          id_risk_form_answer: x.id_risk_form_answer || x.risk_form_answer_id || null,
        }));

        this._rebuildMaps();

        //Build answers + expanded maps
        const answersMap = {};
        const expandedMap = {};

        this.flagged.forEach((item) => {
          answersMap[item.id] = {};

          // Server-confirmed followup answers
          item.followups.forEach((fr) => {
            answersMap[item.id][fr.id_risk_flagged_answers_follow_up_questions] =
              fr.answer || "";
          });

          // Fill empty slots for all active questions
          this.followupQuestionsOrdered.forEach((q) => {
            if (answersMap[item.id][q.id] === undefined) answersMap[item.id][q.id] = "";
          });

          // Restore dirty (user-typed) answers that weren't saved yet
          if (preserveDirty && prevDirty[item.id]) {
            const prevRow = prevAnswers[item.id] || {};
            Object.keys(prevDirty[item.id]).forEach((qid) => {
              answersMap[item.id][qid] = prevRow[qid];
            });
          }

          // Migrate expanded state: pending key TO real id key
          const key = String(item.id);
          const pendingKey = `pending-${item.template_item_id}`;
          expandedMap[key] = preserveOpen
            ? !!(prevExpanded[key] || prevExpanded[pendingKey])
            : false;
        });

        this.answersByFlagged = answersMap;
        this.expanded = expandedMap;
        this.dirty = preserveDirty ? prevDirty : {};
        this.saving = {};

        this.loadedAt = new Date().toISOString();
        this.lastVersionHash = this._computeVersion(this.flagged);
        this.lastLoadOk = true;

        // If still empty after all attempts, show frontend preview as fallback
        if (!this.flagged.length) this.applyFrontFallback();

        this.$emit("changed", { reason: "loadAll" });
      } catch (e) {
        console.error("RiskFlaggedAnswersPart loadAll error", e);
        this.loadError = true;
        this.lastLoadOk = false;

        this.flagged = [];
        this.flaggedById = {};
        this.flaggedByTemplateId = {};
        this.followupQuestions = [];
        this.answersByFlagged = {};
        this.usingFrontFallback = false;
        this.applyFrontFallback();
      } finally {
        this.loading = false;
        this.loadingMode = "idle";
        this._loadingLock = false;
      }
    },

    // ─── UI interaction
    toggle(key) {
      this.$set(this.expanded, key, !this.expanded[key]);
    },

    answeredCount(flaggedId) {
      if (!flaggedId) return 0;
      const row = this.answersByFlagged[flaggedId] || {};
      return this.followupQuestionsOrdered.filter(
        (q) => String(row[q.id] ?? "").trim().length > 0
      ).length;
    },

    getFollowupAnswer(flaggedId, questionId) {
      if (!flaggedId) return "";
      return (this.answersByFlagged[flaggedId] || {})[questionId] ?? "";
    },

    setFollowupAnswer(flaggedId, questionId, value) {
      if (this.usingFrontFallback || !flaggedId) return;

      this.$set(this.answersByFlagged, flaggedId, {
        ...(this.answersByFlagged[flaggedId] || {}),
        [questionId]: value,
      });

      if (!this.dirty[flaggedId]) this.$set(this.dirty, flaggedId, {});
      this.$set(this.dirty[flaggedId], questionId, true);

      this.$emit("followups-changed", { flaggedId, questionId, value });
      this.$emit("dirty", { type: "followup", flaggedId, questionId });
      this.$emit("input", { scope: "flagged.followups", flaggedId, questionId });
      this.$emit("changed", { scope: "flagged.followups", flaggedId, questionId });
    },

    isSaving(flaggedId, questionId) {
      return !!this.saving[flaggedId]?.[questionId];
    },

    hasAnySaving() {
      for (const fid of Object.keys(this.saving || {}))
        for (const qid of Object.keys(this.saving[fid] || {}))
          if (this.saving[fid][qid]) return true;
      return false;
    },

    // ─── Validation
    validateBeforeSave() {
      if (this.usingFrontFallback) return { ok: true, message: "OK" };
      if (!this.flagged.length) return { ok: true, message: "OK" };

      const hasPending =
        Array.isArray(this.frontFlagged) &&
        this.frontFlagged.some(
          (fi) => !this.flaggedByTemplateId[String(fi.template_item_id || "")]
        );

      if (hasPending)
        return {
          ok: false,
          message: this.tf(
            "risk.nona_pending_save",
            "Hay respuestas NO/NA pendientes de sincronizar. Guarda el formulario primero."
          ),
        };

      const requiredIds = this.followupQuestionsOrdered
        .filter((q) => q.is_required)
        .map((q) => q.id);
      for (const item of this.flagged) {
        const row = this.answersByFlagged[item.id] || {};
        for (const rid of requiredIds) {
          if (!String(row[rid] || "").trim())
            return {
              ok: false,
              message: this.tf(
                "risk.nona_required_missing",
                "Faltan respuestas requeridas en NO/NA"
              ),
            };
        }
      }

      return { ok: true, message: "OK" };
    },

    validateBeforeReview() {
      return this.validateBeforeSave();
    },

    // ─── Save followups
    async saveAll() {
      if (this.usingFrontFallback) {
        this.$toast?.warning?.(
          "NO/NA is from frontend preview; follow-ups cannot be saved yet."
        );
        return;
      }

      const payloads = [];
      Object.keys(this.dirty).forEach((fid) => {
        const row = this.flaggedById?.[fid];
        Object.keys(this.dirty[fid] || {}).forEach((qid) => {
          const p = {
            id_risk_flagged_answer: Number(fid),
            id_risk_flagged_answers_follow_up_questions: Number(qid),
            id_risk_evaluation: Number(this.riskEvaluationId),
            answer: this.getFollowupAnswer(Number(fid), Number(qid)),
          };
          if (row?.id_risk_form_answer)
            p.id_risk_form_answer = Number(row.id_risk_form_answer);
          payloads.push(p);
        });
      });

      if (!payloads.length) {
        this.savedFlash = this.tf("common.nothing_to_save", "Nada por guardar");
        setTimeout(() => (this.savedFlash = ""), 1500);
        return;
      }

      for (const p of payloads) {
        this._markSaving(
          p.id_risk_flagged_answer,
          p.id_risk_flagged_answers_follow_up_questions,
          true
        );
        try {
          try {
            await axios.post("/v1/flagged_answers_followups", p, {
              timeout: this.requestTimeoutMs,
            });
          } catch (e) {
            if (e?.response?.status === 404 || e?.response?.status === 405) {
              await axios.post("/v1/flagged_answers_follow_ups", p, {
                timeout: this.requestTimeoutMs,
              });
            } else throw e;
          }
          this._clearDirty(
            p.id_risk_flagged_answer,
            p.id_risk_flagged_answers_follow_up_questions
          );
        } catch (err) {
          console.error("[NONA] followup save error:", err?.response || err);
          this.$toast?.error?.(
            err?.response?.data?.message ||
              this.tf("risk.save_failed", "No se pudo guardar")
          );
        } finally {
          this._markSaving(
            p.id_risk_flagged_answer,
            p.id_risk_flagged_answers_follow_up_questions,
            false
          );
        }
      }

      await this.refreshIfChanged({ mode: "soft" });
      this.savedFlash = this.tf("custom_forms.answers_saved", "Respuestas guardadas");
      setTimeout(() => (this.savedFlash = ""), 1500);
      this.$emit("changed", { scope: "flagged.saved" });
    },

    _markSaving(fid, qid, state) {
      if (state) {
        if (!this.saving[fid]) this.$set(this.saving, fid, {});
        this.$set(this.saving[fid], qid, true);
      } else if (this.saving[fid]) {
        this.$delete(this.saving[fid], qid);
        if (!Object.keys(this.saving[fid]).length) this.$delete(this.saving, fid);
      }
    },

    _clearDirty(fid, qid) {
      if (this.dirty[fid]) {
        this.$delete(this.dirty[fid], qid);
        if (!Object.keys(this.dirty[fid]).length) this.$delete(this.dirty, fid);
      }
    },

    clearSaving() {
      this.saving = {};
      this.loading = false;
      this.loadingMode = "idle";
      this._loadingLock = false;
    },
  },
};
</script>

<style scoped>
.flagged-card {
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 8px;
  overflow: hidden;
}

.flagged-header {
  padding: 10px 14px;
  cursor: pointer;
  user-select: none;
  transition: background 0.1s;
}
.flagged-header:hover {
  background: #f8f9fa;
}

.chev {
  flex-shrink: 0;
  margin-top: 5px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 7px solid #6b7280;
  transition: transform 0.15s ease;
}
.chev.open {
  transform: rotate(180deg);
}


.followups {
  padding: 0 14px 14px;
}
.followup-item + .followup-item {
  margin-top: 10px;
}

.fw-600 {
  font-weight: 600;
}

.rfm-relative {
  position: relative;
}

.soft-indicator {
  position: absolute;
  top: 0;
  right: 0;
  padding: 3px 6px;
  z-index: 2;
  pointer-events: none; /* never captures clicks or keyboard events */
  opacity: 0.6;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.pending-badge {
  display: inline-flex;
  align-items: center;
  opacity: 0.75;
}
</style>
